import { Toaster } from '@src/components/ui/sonner';

import { PortalContainerProvider } from '@shared/providers/PortalContainerContext';
import { ThemeType, useThemeStore } from '@shared/stores/theme';

import {
    registerEmotionCssContainer,
    unregisterEmotionCssContainer,
} from './emotion-css';
import wcStyles from './styles.css?inline';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import geomanStyles from '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css?inline';
import { createRoot, Root } from 'react-dom/client';

type SharedRuntime = {
    emotionContainer: HTMLDivElement;
    host: HTMLDivElement;
    portalContainer: HTMLDivElement;
    portalEmotionCache: ReturnType<typeof createCache>;
    refs: number;
    toasterRoot: Root;
    unsubscribeTheme: () => void;
};

export type WebComponentRuntime = {
    emotionCache: ReturnType<typeof createCache>;
    portalContainer: HTMLDivElement;
    portalEmotionCache: ReturnType<typeof createCache>;
    setActive: (active: boolean) => void;
    release: () => void;
};

let sharedRuntime: SharedRuntime | null = null;
let sharedStyleSheet: CSSStyleSheet | null | undefined;
const staticStyles = `${wcStyles}\n${geomanStyles}`;

const applyThemeMode = (node: HTMLElement, theme: ThemeType) => {
    node.dataset.theme = theme;
    node.classList.toggle('dark', theme === 'dark');
};

export const setWebComponentTheme = (theme?: string | null) => {
    if (theme !== 'dark' && theme !== 'light') return;
    if (useThemeStore.getState().theme === theme) return;

    useThemeStore.getState().setTheme(theme);
};

const getSharedStyleSheet = () => {
    if (sharedStyleSheet !== undefined) return sharedStyleSheet;

    const supportsConstructableStylesheets =
        typeof CSSStyleSheet !== 'undefined' &&
        typeof CSSStyleSheet.prototype.replaceSync === 'function' &&
        typeof ShadowRoot !== 'undefined' &&
        'adoptedStyleSheets' in ShadowRoot.prototype;

    if (!supportsConstructableStylesheets) {
        sharedStyleSheet = null;
        return sharedStyleSheet;
    }

    try {
        sharedStyleSheet = new CSSStyleSheet();
        sharedStyleSheet.replaceSync(staticStyles);
    } catch {
        sharedStyleSheet = null;
    }

    return sharedStyleSheet;
};

const attachStaticStyles = (shadowRoot: ShadowRoot) => {
    const styleSheet = getSharedStyleSheet();

    if (styleSheet) {
        if (!shadowRoot.adoptedStyleSheets.includes(styleSheet)) {
            shadowRoot.adoptedStyleSheets = [
                ...shadowRoot.adoptedStyleSheets,
                styleSheet,
            ];
        }

        return () => {
            shadowRoot.adoptedStyleSheets =
                shadowRoot.adoptedStyleSheets.filter(
                    (currentStyleSheet) => currentStyleSheet !== styleSheet,
                );
        };
    }

    const style = document.createElement('style');
    style.dataset.officeWcStaticStyles = '';
    style.textContent = staticStyles;
    shadowRoot.prepend(style);

    return () => style.remove();
};

const createSharedRuntime = (): SharedRuntime => {
    const host = document.createElement('div');
    host.dataset.officeWcOverlayHost = '';
    Object.assign(host.style, {
        position: 'fixed',
        inset: '0',
        width: '100vw',
        height: '100vh',
        zIndex: '50',
        pointerEvents: 'none',
    });

    const shadowRoot = host.attachShadow({ mode: 'open' });
    attachStaticStyles(shadowRoot);

    const emotionContainer = document.createElement('div');
    emotionContainer.dataset.officeWcPortalEmotionStyles = '';

    const portalContainer = document.createElement('div');
    portalContainer.dataset.scopeOffice = '';
    portalContainer.dataset.officeWcPortals = '';
    portalContainer.style.pointerEvents = 'auto';

    const toasterContainer = document.createElement('div');
    toasterContainer.dataset.scopeOffice = '';
    toasterContainer.dataset.officeWcToaster = '';
    toasterContainer.style.pointerEvents = 'auto';

    const theme = useThemeStore.getState().theme;
    applyThemeMode(portalContainer, theme);
    applyThemeMode(toasterContainer, theme);

    shadowRoot.append(emotionContainer, portalContainer, toasterContainer);
    (document.body ?? document.documentElement).appendChild(host);

    registerEmotionCssContainer(emotionContainer);

    const portalEmotionCache = createCache({
        key: 'office-wc-react-portal',
        container: emotionContainer,
    });
    const toasterRoot = createRoot(toasterContainer);
    toasterRoot.render(
        <CacheProvider value={portalEmotionCache}>
            <PortalContainerProvider
                value={{
                    container: portalContainer,
                    emotionCache: portalEmotionCache,
                }}
            >
                <Toaster />
            </PortalContainerProvider>
        </CacheProvider>,
    );

    const unsubscribeTheme = useThemeStore.subscribe((state) => {
        applyThemeMode(portalContainer, state.theme);
        applyThemeMode(toasterContainer, state.theme);
    });

    return {
        emotionContainer,
        host,
        portalContainer,
        portalEmotionCache,
        refs: 0,
        toasterRoot,
        unsubscribeTheme,
    };
};

const destroySharedRuntime = (runtime: SharedRuntime) => {
    if (sharedRuntime !== runtime || runtime.refs !== 0) return;

    runtime.unsubscribeTheme();
    unregisterEmotionCssContainer(runtime.emotionContainer);
    runtime.toasterRoot.unmount();
    runtime.host.remove();
    sharedRuntime = null;
};

export const acquireWebComponentRuntime = (
    shadowRoot: ShadowRoot,
    emotionContainer: HTMLDivElement,
): WebComponentRuntime => {
    const runtime = sharedRuntime ?? createSharedRuntime();
    sharedRuntime = runtime;
    runtime.refs += 1;

    const detachStaticStyles = attachStaticStyles(shadowRoot);
    registerEmotionCssContainer(emotionContainer);

    // Each WC owns its portal subtree, so cached pages can hide it independently.
    const portalContainer = document.createElement('div');
    portalContainer.dataset.officeWcPortalOwner = '';
    runtime.portalContainer.appendChild(portalContainer);

    let released = false;

    return {
        emotionCache: createCache({
            key: 'office-wc-react',
            container: emotionContainer,
        }),
        portalContainer,
        setActive: (active) => {
            portalContainer.hidden = !active;
        },
        portalEmotionCache: runtime.portalEmotionCache,
        release: () => {
            if (released) return;
            released = true;

            portalContainer.remove();
            detachStaticStyles();
            unregisterEmotionCssContainer(emotionContainer);
            runtime.refs -= 1;

            if (runtime.refs === 0) {
                queueMicrotask(() => destroySharedRuntime(runtime));
            }
        },
    };
};
